import * as React from 'react';

/** Roughly 160ms at 60Hz. Long enough for a history pop, short enough not to steal a keystroke. */
export const FOCUS_RETURN_FRAME_BUDGET = 10;

/**
 * **Where the keyboard goes when a routed overlay closes** (`PLT-007` AC-3, WCAG 2.4.3).
 *
 * ## The bug this exists for
 *
 * Radix returns focus to whatever opened a dialog: it keeps a reference to the element that had
 * focus when the dialog mounted, and focuses it again on unmount. That is correct, and for the
 * overlays this app opens from local state — the QR code, the scanner, a note composer — it works,
 * because the trigger is still the same DOM node when the dialog goes away.
 *
 * **Our detail overlays are routes.** The thread lives at `/app/messages/:ref`, the attendee card
 * at `/app/events/:id/attendees/:ref`, the ticket at `/app/events/:id/ticket`. Closing one calls
 * `navigate(...)`, React Router re-renders the list underneath, and the row that opened the overlay
 * is **unmounted and re-created** — a new node, at the same address. The reference Radix held is
 * now detached, `HTMLElement.focus()` on a detached node does nothing, and focus falls to
 * `<body>`. The next Tab starts again from the skip link, at the top of the page, however far down
 * the list the reader was.
 *
 * Found in the parity pass of 2026-09-12 and verified on two of them, which is the part worth
 * keeping: `thread-dialog.tsx` and `ticket-dialog.tsx` **both stated in their headers that focus
 * returned to the control that opened them.** It was specified, written down as done, and did not
 * happen. Only opening the app and pressing Escape says which.
 *
 * ## Why the overlay's own address is the identity
 *
 * Nothing has to be threaded through the route state, because the trigger identifies itself: a
 * routed overlay is opened by **a link whose `href` is the overlay's own path**. After the
 * navigation that link is a different node with the same `href`, so the address is a stable handle
 * across exactly the remount that broke the reference. `[data-overlay-return]` is honoured first
 * for a trigger that is not an anchor — a button that navigates, say — but no caller needs it yet.
 *
 * ## The ladder, and why it ends where it does
 *
 * The trigger, then the page's `h1`, then `<main>`. Never `<body>`: focus on the body is focus
 * nowhere, and it is indistinguishable from a page that has just loaded. `<main>` already carries
 * `tabIndex={-1}` for the skip link (`every-layout-can-be-skipped-past.test.tsx`), and the heading
 * is given it here for the moment it is focused and then has it taken away again, so it never
 * becomes a tab stop of its own.
 *
 * ## Timing, and why it is a poll rather than a delay
 *
 * `onCloseAutoFocus` fires while the dialog is unmounting, before the route the caller navigated to
 * has painted, so restoring there would search a DOM that does not hold the row yet. The event is
 * cancelled — that is what stops Radix focusing the stale node — and the search runs after.
 *
 * **After how long is not a constant, and assuming it was cost a working fix.** The first version
 * waited two animation frames, which is enough for `navigate(path)`: React Router pushes, React
 * re-renders, the row is there. It is *not* enough for `navigate(-1)`, which the two detail
 * overlays use — a history pop is handled by the browser and the list came back a frame or two
 * later, so the search ran against a DOM that still had no row and quietly fell through to the
 * heading. The thread dialog passed and the attendee card failed, on the same code.
 *
 * So it polls: every frame until the trigger appears or {@link FOCUS_RETURN_FRAME_BUDGET} frames
 * have passed, and then the fallback. A poll is right here rather than lazy, because the thing
 * being waited for is *another framework's* commit and neither its timing nor its number of passes
 * is ours to know. The budget is about a sixth of a second — long enough for a slow pop, short
 * enough that a reader who has already pressed Tab is not fighting it.
 */

export function useOverlayFocusReturn(overlayPath: string | null): {
  /** Spread onto `DialogContent` / `DialogBareContent`. */
  readonly onCloseAutoFocus: (event: Event) => void;
} {
  const onCloseAutoFocus = React.useCallback(
    (event: Event) => {
      // Radix would focus the node it remembers, which the navigation has already detached.
      event.preventDefault();
      restoreFocusAfterOverlay(overlayPath);
    },
    [overlayPath],
  );

  return { onCloseAutoFocus };
}

/**
 * **The same bug, reached from the other direction: an overlay that mounts already open.**
 *
 * `note-composer-dialog.tsx` and its four siblings are not routes. They are a `<Dialog open>`
 * rendered only while a piece of state is set — `if (person === null) return null` — so the whole
 * Radix root **mounts in the open state**. Radix records the element to return to when the dialog
 * *opens*, and from its point of view this one never opened: it was always open. So there is
 * nothing to restore, and focus went to `<body>` exactly as it did from the routed ones.
 *
 * Found by probing each overlay in the browser rather than by reading them, which is the only way
 * this one shows up: the file looks nothing like the routed case and fails identically.
 *
 * Here the trigger is still in the document — the button on the card is not going anywhere — so the
 * fix is to remember the element rather than an address. It is captured on the render where `open`
 * becomes true, which is after the click that set the state and before Radix moves focus inside.
 *
 * **`open` is a parameter and not an assumption**, because the five overlays differ in a way that
 * matters: the follow-up composer is *mounted* only while it is open, so its first render is the
 * moment it opened; the note composer stays mounted and renders `null`, so its first render is the
 * page load, when the element with focus is whatever the member last touched — or nothing at all.
 * Capturing on first render unconditionally would have remembered the wrong element for one of the
 * two and looked right in both.
 *
 * **Not every already-open dialog needs this.** `connect-request-dialog.tsx` and
 * `connection-note-dialog.tsx` take an `open` prop and stay mounted, so Radix *does* see the
 * transition and records the trigger itself; both were probed and both were already correct, and
 * they are deliberately left alone rather than given a second implementation of working behaviour.
 *
 * Returns the handler **and** a `restore` for an overlay that is not a Radix dialog at all; the
 * in-event scanner is a hand-rolled `<div role="dialog">` and has to call it from its own unmount.
 */
export function useReturnFocusToOpener(open: boolean): {
  readonly onCloseAutoFocus: (event: Event) => void;
  readonly restore: () => void;
} {
  const opener = React.useRef<HTMLElement | null>(null);
  const wasOpen = React.useRef(false);
  if (open && !wasOpen.current) {
    const active = typeof document === 'undefined' ? null : document.activeElement;
    // `<body>` is what `document.activeElement` reports when nothing is focused, and it is the one
    // answer this whole module exists to refuse.
    opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }
  wasOpen.current = open;

  const restore = React.useCallback(() => {
    const target = opener.current;
    if (target !== null && target.isConnected) {
      target.focus();
      return;
    }
    // The opener went away with the state that opened it — a card that was removed, say.
    const fallback = overlayReturnFallback();
    if (fallback !== null) focusWithoutBecomingATabStop(fallback);
  }, []);

  const onCloseAutoFocus = React.useCallback(
    (event: Event) => {
      event.preventDefault();
      restore();
    },
    [restore],
  );

  return { onCloseAutoFocus, restore };
}

/**
 * Exported for the test, which drives it against a DOM rather than reading this file — a focus
 * rule asserted by a regular expression over source is a rule nobody has run.
 *
 * `nextFrame` is injectable for the same reason: a test that had to wait on real animation frames
 * would be a slow test that passes on a fast machine.
 */
export function restoreFocusAfterOverlay(
  overlayPath: string | null,
  nextFrame: (run: () => void) => void = requestFrame,
): void {
  let framesLeft = FOCUS_RETURN_FRAME_BUDGET;
  const attempt = (): void => {
    const trigger = overlayTrigger(overlayPath);
    if (trigger !== null) {
      focusWithoutBecomingATabStop(trigger);
      return;
    }
    framesLeft -= 1;
    if (framesLeft > 0) {
      nextFrame(attempt);
      return;
    }
    // The row never came back — a list that is still loading, or an overlay opened from an address
    // the reader typed. The page's own heading is where a keyboard user expects to land after
    // leaving something, and it is never `<body>`.
    const fallback = overlayReturnFallback();
    if (fallback !== null) focusWithoutBecomingATabStop(fallback);
  };
  nextFrame(attempt);
}

/**
 * The control that opened the overlay: the element tagged for it, or the link pointing at the
 * overlay's own address. `null` when neither is in the document *yet* — which is the whole reason
 * the caller polls.
 */
export function overlayTrigger(overlayPath: string | null): HTMLElement | null {
  if (overlayPath === null || overlayPath.length === 0) return null;
  const escaped = cssEscape(overlayPath);
  return (
    document.querySelector<HTMLElement>(`[data-overlay-return="${escaped}"]`) ??
    document.querySelector<HTMLElement>(`a[href="${escaped}"]`)
  );
}

/** Where focus goes when the trigger is gone for good. Never `<body>`. */
export function overlayReturnFallback(): HTMLElement | null {
  const main = document.querySelector<HTMLElement>('main');
  return main?.querySelector<HTMLElement>('h1') ?? main;
}

/**
 * `<main>` and a heading are not controls, and must not become tab stops because focus rested on
 * them once. They are made focusable for the call and released immediately after.
 */
function focusWithoutBecomingATabStop(target: HTMLElement): void {
  const borrowed = target.getAttribute('tabindex') === null && !isNativelyFocusable(target);
  if (borrowed) target.setAttribute('tabindex', '-1');
  target.focus();
  if (borrowed) target.removeAttribute('tabindex');
}

function isNativelyFocusable(element: HTMLElement): boolean {
  return ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName);
}

/** `CSS.escape` where it exists, and a conservative fallback for the test environment. */
function cssEscape(value: string): string {
  const escape = (globalThis as { CSS?: { escape?: (input: string) => string } }).CSS?.escape;
  return escape === undefined ? value.replace(/["\\]/gu, '\\$&') : escape(value);
}

function requestFrame(run: () => void): void {
  const raf = globalThis.requestAnimationFrame?.bind(globalThis);
  if (raf === undefined) {
    setTimeout(run, 0);
    return;
  }
  raf(run);
}
